import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GridDataResult } from '@progress/kendo-angular-grid';
import {
  toDataSourceRequestString,
  translateDataSourceResultGroups,
} from '@progress/kendo-data-query';
import { ListViewDataResult } from '@progress/kendo-angular-listview';
import { map, Observable } from 'rxjs';
import { IChangeset } from '@shared-data';
import { environment } from 'src/environments/environment';
import { GridRowOverride } from '../models';
import { GridState } from '@shared';
import { ExportActiveFilter, ExportOption, ImportOptions } from '../constants';

@Injectable()
export class RestaurantItemService {
  apiUrl: string = environment.apiUrl + '/restaurantitem';
  constructor(private httpClient: HttpClient) {}

  getChangesets(type: string, state: GridState): Observable<GridDataResult> {
    const hasGroups = state.group && state.group.length;
    if (state.restaurantIds && state.restaurantIds.length > 0) {
      const restaurantIds = state.restaurantIds?.join(',');
      return this.httpClient
        .get<GridDataResult>(
          `${this.apiUrl}/${state.productType}/${type}?${toDataSourceRequestString(state)}&restaurantIds=${restaurantIds}`,
        )
        .pipe(
          map(({ data, total }) => ({
            data: hasGroups ? translateDataSourceResultGroups(data) : data,
            total: total,
          })),
        );
    }

    return this.httpClient
      .get<GridDataResult>(
        `${this.apiUrl}/${state.productType}/${type}?${toDataSourceRequestString(state)}&restaurantId=${state.restaurantId}`,
      )
      .pipe(
        map(({ data, total }) => ({
          data: hasGroups ? translateDataSourceResultGroups(data) : data,
          total: total,
        })),
      );
  }

  saveChangesets(
    type: string,
    changesets: IChangeset[],
    productType: string,
    applyToRestaurants?: string[],
  ): Observable<any> {
    const url = applyToRestaurants
      ? `${this.apiUrl}/${productType}/${type}/bulk?applyToRestaurants=${applyToRestaurants.join(',')}`
      : `${this.apiUrl}/${productType}/${type}/bulk`;
    return this.httpClient.post<any>(url, changesets);
  }

  exportToExcel(
    type: string,
    restaurantId: string,
    productType: string,
    exportOption: ExportOption,
    activeFilter?: ExportActiveFilter,
  ): Observable<Blob> {
    // Only the pricing exports take the filter; without it the API exports active items only
    const filter = activeFilter ? `&activeFilter=${activeFilter}` : '';
    switch (exportOption) {
      case 'Current Type':
      default:
        return this.httpClient.get(
          `${this.apiUrl}/${productType}/${type}/export?restaurantId=${restaurantId}${filter}`,
          {
            responseType: 'blob',
          },
        );
      case 'Current Type (All Tiers)':
        return this.httpClient.get(
          `${this.apiUrl}/${productType}/pricing/exportalltiers?restaurantId=${restaurantId}${filter}`,
          {
            responseType: 'blob',
          },
        );
      case 'Tier':
        return this.httpClient.get(
          `${this.apiUrl}/all/pricing/exporttier?restaurantId=${restaurantId}${filter}`,
          {
            responseType: 'blob',
          },
        );
      case 'All':
        return this.httpClient.get(
          `${this.apiUrl}/all/pricing/exportbytype${activeFilter ? `?activeFilter=${activeFilter}` : ''}`,
          {
            responseType: 'blob',
          },
        );
    }
  }

  getDistinctItems(
    state: GridState,
    type: string,
    field: string,
    restaurantId?: string,
  ): Observable<ListViewDataResult> {
    const url = restaurantId
      ? `${this.apiUrl}/${state.productType}/${type}/distinctItems/${field}?${toDataSourceRequestString(state)}&restaurantId=${restaurantId}`
      : `${this.apiUrl}/${state.productType}/${type}/distinctItems/${field}?${toDataSourceRequestString(state)}`;
    return this.httpClient.get<ListViewDataResult>(url);
  }

  getImportUrls(productType: string, type: string): Map<ImportOptions, string> {
    return new Map<ImportOptions, string>([
      ['Current Type', `${this.apiUrl}/${productType}/${type}/import`],
      ['All', `${this.apiUrl}/all/pricing/importbytype`],
    ]);
  }

  getOverrides(
    state: GridState,
    componentId: number,
    onlyEntrees: boolean,
  ): any {
    return this.httpClient.get<GridDataResult>(
      `${this.apiUrl}/overrides?${toDataSourceRequestString(state)}&restaurantId=${state.restaurantId}&componentId=${componentId}&onlyEntrees=${onlyEntrees}`,
    );
  }

  saveOverrides(changesets: GridRowOverride[]): Observable<IChangeset[]> {
    const url = `${this.apiUrl}/overrides/bulk`;
    return this.httpClient.post<IChangeset[]>(url, changesets);
  }

  loadRbiRecommendations(restaurantId: string, productType: string): Observable<void> {
    return this.httpClient.post<void>(`${this.apiUrl}/${productType}/pricing/loadRecommendations?restaurantId=${restaurantId}`, {});
  }

  getBulkDeliveryPctChangeCount(
    tierId: number,
    productTypeId: string,
    categoryId: number | null,
    comboParId: number | null,
  ): Observable<{ count: number }> {
    let url = `${this.apiUrl}/pricing/bulk-delivery-pct-change/count?tierId=${tierId}&productTypeId=${productTypeId}`;
    if (categoryId != null) url += `&categoryId=${categoryId}`;
    if (comboParId != null) url += `&comboParId=${comboParId}`;
    return this.httpClient.get<{ count: number }>(url);
  }

  applyBulkDeliveryPctChange(request: {
    tierId: number;
    productTypeId: string;
    categoryId: number | null;
    comboParId: number | null;
    amountChange: number;
    changeType: string;
    roundDirection: string;
    roundToNearest: number;
  }): Observable<{ updatedCount: number }> {
    return this.httpClient.post<{ updatedCount: number }>(
      `${this.apiUrl}/pricing/bulk-delivery-pct-change`,
      request,
    );
  }

  getComboEntrees(tierId: number): Observable<{ parItemId: string; itemName: string }[]> {
    return this.httpClient.get<{ parItemId: string; itemName: string }[]>(
      `${this.apiUrl}/pricing/bulk-delivery-pct-change/combo-entrees?tierId=${tierId}`,
    );
  }
}
